import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Flex, Row, Select, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { sanitizedFilter } from "../../common/functions.ts";
import { dashboardActions } from "../../features/dashboard/dashboardSlice";
import { ConnectedAppLayout } from "../AppLayout";
import { routeNames } from "../common/routes";
import type { RootState } from "../store";
import { palette } from "../theme";
import { standbyReasonLabelMap } from "../ticket/ticketConstants";
import { AttentionCard } from "./components/AttentionCard";
import { ProjectProgressTable } from "./components/ProjectProgressTable";
import { SuppliesThisMonthCard } from "./components/SuppliesThisMonthCard";
import { TechnicianWorkloadTable } from "./components/TechnicianWorkloadTable";
import { ThroughputChart } from "./components/ThroughputChart";
import { TodayInFieldCard } from "./components/TodayInFieldCard";
import { WorkOrdersByServiceChart } from "./components/WorkOrdersByServiceChart";
import { WorkOrdersByStatusChart } from "./components/WorkOrdersByStatusChart";
import styles from "./Dashboard.module.css";
import {
  AUTO_REFRESH_MINUTES,
  DUE_SOON_DAYS,
  LONG_OVERDUE_DAYS,
  STARTING_SOON_DAYS,
  WO_STATUS_COLORS,
} from "./dashboard.constants";

type WorkOrderListFilter = {
  attention?: string;
  status?: string;
  service?: string;
  assignees?: string;
  project?: string;
  sort?: string;
};

type DashboardPageProps = ReturnType<typeof withDashboard> & DispatchProp;

export const DashboardPage = (props: DashboardPageProps) => {
  const { intl, dispatch, summary, status, loadedAt, projects, currentRoute } = props;
  const project: string | undefined = currentRoute?.params.project || undefined;
  const loading = status === "loading" && summary === null;

  // A hidden tab skips the refresh; the next one after it is shown catches up.
  useEffect(() => {
    const interval = window.setInterval(
      () => {
        if (document.visibilityState === "visible") {
          dispatch(dashboardActions.fetchDashboard({ project }));
        }
      },
      AUTO_REFRESH_MINUTES * 60 * 1000
    );
    return () => window.clearInterval(interval);
  }, [dispatch, project]);

  const projectOptions = useMemo(
    () =>
      (projects ?? [])
        .map((item) => ({ value: item.id, label: item.name ?? "—" }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [projects]
  );

  // Every link keeps the dashboard's project filter.
  const openWorkOrders = (filter: WorkOrderListFilter) =>
    dispatch(
      routerActions.navigateTo(
        routeNames.TICKET_LIST,
        sanitizedFilter({ project, ...filter })
      )
    );
  const navigateTo = (route: string, params?: Record<string, string>) =>
    dispatch(routerActions.navigateTo(route, params));

  const attention = summary?.attention;
  const awaitingReviewHint = attention?.awaiting_review_oldest_since
    ? intl.formatMessage(
        { id: "dashboard_attention_awaiting_review_oldest" },
        {
          days: dayjs()
            .startOf("day")
            .diff(dayjs(attention.awaiting_review_oldest_since).startOf("day"), "day"),
        }
      )
    : intl.formatMessage({ id: "dashboard_attention_awaiting_review_hint" });
  const delayedHint =
    summary && summary.delayed_by_reason.length > 0
      ? summary.delayed_by_reason
          .map(
            ({ reason, count }) =>
              `${(reason && standbyReasonLabelMap[reason]) ?? "—"} ${count}`
          )
          .join(" · ")
      : intl.formatMessage({ id: "dashboard_attention_delayed_none" });

  const attentionCards = [
    {
      key: "overdue",
      title: intl.formatMessage({ id: "dashboard_attention_overdue" }),
      value: attention?.overdue,
      hint: attention?.long_overdue
        ? intl.formatMessage(
            { id: "dashboard_attention_overdue_long" },
            { count: attention.long_overdue, days: LONG_OVERDUE_DAYS }
          )
        : intl.formatMessage({ id: "dashboard_attention_overdue_hint" }),
      accentColor: palette.dashRed,
      filter: { attention: "overdue", sort: "deadline" },
    },
    {
      key: "due_soon",
      title: intl.formatMessage({ id: "dashboard_attention_due_soon" }),
      value: attention?.due_soon,
      hint: intl.formatMessage(
        { id: "dashboard_attention_due_soon_hint" },
        { days: DUE_SOON_DAYS }
      ),
      accentColor: palette.dashOrange,
      filter: { attention: "due_soon", sort: "deadline" },
    },
    {
      key: "awaiting_review",
      title: intl.formatMessage({ id: "dashboard_attention_awaiting_review" }),
      value: attention?.awaiting_review,
      hint: awaitingReviewHint,
      accentColor: WO_STATUS_COLORS.IN_REVIEW,
      // longest waiting first
      filter: { status: "IN_REVIEW", sort: "sent_to_review_at" },
    },
    {
      key: "delayed",
      title: intl.formatMessage({ id: "dashboard_attention_delayed" }),
      value: attention?.delayed,
      hint: delayedHint,
      accentColor: WO_STATUS_COLORS.DELAYED,
      filter: { status: "DELAYED" },
    },
    {
      key: "on_standby",
      title: intl.formatMessage({ id: "dashboard_attention_on_standby" }),
      value: attention?.on_standby,
      hint: intl.formatMessage({ id: "dashboard_attention_on_standby_hint" }),
      accentColor: WO_STATUS_COLORS.STANDBY,
      filter: { status: "STANDBY" },
    },
    {
      key: "unstaffed",
      title: intl.formatMessage({ id: "dashboard_attention_unstaffed" }),
      value: attention?.unstaffed,
      hint: intl.formatMessage(
        { id: "dashboard_attention_unstaffed_hint" },
        { count: attention?.unstaffed_starting_soon ?? 0, days: STARTING_SOON_DAYS }
      ),
      accentColor: palette.dashBlue,
      filter: { attention: "unstaffed", sort: "start" },
    },
  ];

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "dashboard_title" })}
        subtitle={intl.formatMessage({ id: "dashboard_subtitle" })}
        extra={
          <Flex gap="small" align="center" wrap>
            {loadedAt && (
              <Typography.Text type="secondary">
                {intl.formatMessage(
                  { id: "dashboard_updated_at" },
                  {
                    time: intl.formatMessage(
                      { id: "time" },
                      { value: new Date(loadedAt) }
                    ),
                  }
                )}
              </Typography.Text>
            )}
            <Select
              className={styles.projectFilter}
              allowClear
              showSearch
              optionFilterProp="label"
              loading={projects === undefined}
              placeholder={intl.formatMessage({ id: "dashboard_all_projects" })}
              options={projectOptions}
              value={project}
              onChange={(value) =>
                navigateTo(routeNames.DASHBOARD, sanitizedFilter({ project: value }))
              }
            />
            <Button
              icon={<ReloadOutlined />}
              loading={status === "loading"}
              onClick={() => dispatch(dashboardActions.fetchDashboard({ project }))}
            >
              {intl.formatMessage({ id: "dashboard_refresh" })}
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap="middle">
          {status === "failed" && (
            <Alert
              type="error"
              showIcon
              title={intl.formatMessage({ id: "dashboard_load_failed" })}
            />
          )}

          <Row gutter={[16, 16]}>
            {attentionCards.map(({ key, filter, ...card }) => (
              <Col key={key} xs={24} sm={12} lg={8} xxl={4}>
                <AttentionCard
                  {...card}
                  loading={loading}
                  onOpen={() => openWorkOrders(filter)}
                />
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={16}>
              <TodayInFieldCard
                intl={intl}
                rows={summary?.field_today}
                loading={loading}
                onOpenWorkOrder={(ticketId) =>
                  navigateTo(routeNames.TICKET_VIEW, { id: ticketId })
                }
              />
            </Col>
            <Col xs={24} xl={8}>
              <WorkOrdersByStatusChart
                intl={intl}
                counts={summary?.open_by_status}
                total={summary?.open_total ?? 0}
                loading={loading}
                onOpenStatus={(workOrderStatus) =>
                  openWorkOrders({ status: workOrderStatus })
                }
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={16}>
              <TechnicianWorkloadTable
                intl={intl}
                rows={summary?.workload}
                loading={loading}
                onOpenSchedule={() => navigateTo(routeNames.SCHEDULE)}
                onOpenTechnicianWorkOrders={(technicianId) =>
                  openWorkOrders({ assignees: String(technicianId) })
                }
              />
            </Col>
            <Col xs={24} xl={8}>
              <WorkOrdersByServiceChart
                intl={intl}
                entries={summary?.open_by_service}
                total={summary?.open_total ?? 0}
                loading={loading}
                onOpenService={(serviceId) => openWorkOrders({ service: serviceId })}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={16}>
              <ThroughputChart
                intl={intl}
                throughput={summary?.throughput}
                loading={loading}
              />
            </Col>
            <Col xs={24} xl={8}>
              <SuppliesThisMonthCard
                intl={intl}
                supplies={summary?.supplies_this_month}
                loading={loading}
                onOpenReport={() => navigateTo(routeNames.REPORT_MATERIAL_USAGE)}
              />
            </Col>
          </Row>

          <ProjectProgressTable
            intl={intl}
            rows={summary?.project_progress}
            loading={loading}
            onOpenProject={(projectId) =>
              navigateTo(routeNames.PROJECT_VIEW, { id: projectId })
            }
            onOpenWorkOrders={openWorkOrders}
          />
        </Flex>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

const withDashboard = (state: RootState) => ({
  intl: state.intl.datetimeIntl,
  summary: state.dashboard.summary,
  status: state.dashboard.status,
  loadedAt: state.dashboard.loadedAt,
  projects: state.ticketData.projects,
  currentRoute: state.router.route,
});

export default connect(withDashboard)(DashboardPage);
